import React, { useEffect, useState, useCallback } from 'react';
import { Mail, Users, Building2, Loader2, Send, BarChart3, Eye, MousePointerClick, RefreshCw, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';
import { api, friendlyError } from '../../lib/api';

const SEGMENT_LABEL = { agents: 'Asesores', agencies: 'Inmobiliarias' };

export default function CampaignsPanel() {
  const { toast } = useToast();
  const [audience, setAudience] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(null); // 'agents'|'agencies'|'both' while in-flight
  const [confirming, setConfirming] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, h] = await Promise.all([api.campaignsAudience(), api.campaignsHistory()]);
      setAudience(a);
      setHistory(h.items || []);
    } catch (e) {
      toast({ title: 'Error', description: friendlyError(e, 'No pudimos cargar las campañas.') });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const send = async (target, dryRun = false) => {
    setSending(target);
    setConfirming(null);
    try {
      const r = await api.campaignsSend({ target, dry_run: dryRun });
      setLastResult(r);
      const totalSent = Object.values(r.segments || {}).reduce((s, v) => s + (v.sent_ok || 0), 0);
      const totalEligible = Object.values(r.segments || {}).reduce((s, v) => s + (v.eligible || 0), 0);
      toast({
        title: dryRun ? 'Simulación completada' : `${totalSent} correo${totalSent !== 1 ? 's' : ''} enviado${totalSent !== 1 ? 's' : ''}`,
        description: totalEligible === 0
          ? 'Todos los destinatarios ya recibieron esta campaña.'
          : `${totalEligible} elegibles · ${totalSent} enviados con éxito.`,
      });
      await load();
    } catch (e) {
      toast({ title: 'Error al enviar', description: friendlyError(e, 'No pudimos enviar la campaña.') });
    } finally {
      setSending(null);
    }
  };

  if (loading && !audience) {
    return <div className="bg-white rounded-2xl p-12 text-center text-gray-500" data-testid="campaigns-loading"><Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Cargando…</div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-campaigns-panel">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-2xl font-bold text-habita-dark flex items-center gap-2"><Mail className="w-6 h-6 text-habita" /> Campañas de Email</h2>
          <p className="text-sm text-gray-500 mt-1">Activación de usuarios registrados. Cada usuario recibe la campaña una sola vez.</p>
        </div>
        <Button variant="outline" onClick={load} className="rounded-full" data-testid="campaigns-refresh-btn">
          <RefreshCw className="w-4 h-4 mr-1.5" /> Refrescar
        </Button>
      </div>

      {/* Audience cards */}
      <div className="grid md:grid-cols-2 gap-4" data-testid="campaigns-audience-grid">
        <AudienceCard
          icon={<Users className="w-5 h-5" />}
          label="Asesores"
          counts={audience?.agents}
          target="agents"
          sending={sending}
          onSend={() => setConfirming('agents')}
        />
        <AudienceCard
          icon={<Building2 className="w-5 h-5" />}
          label="Inmobiliarias"
          counts={audience?.agencies}
          target="agencies"
          sending={sending}
          onSend={() => setConfirming('agencies')}
        />
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 p-5 flex flex-wrap items-center gap-3" data-testid="campaigns-bulk-actions">
        <span className="text-sm text-gray-700">¿Enviar a ambos segmentos a la vez?</span>
        <Button
          onClick={() => setConfirming('both')}
          disabled={!!sending}
          className="btn-habita rounded-full"
          data-testid="campaigns-send-both-btn"
        >
          {sending === 'both' ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Send className="w-4 h-4 mr-1.5" />}
          Enviar a Asesores + Inmobiliarias
        </Button>
        <Button
          variant="outline"
          onClick={() => send('both', true)}
          disabled={!!sending}
          className="rounded-full"
          data-testid="campaigns-dryrun-btn"
        >
          Simular envío
        </Button>
      </div>

      {/* Confirm dialog */}
      {confirming && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex items-start gap-4" data-testid="campaigns-confirm">
          <AlertCircle className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-habita-dark">Confirmar envío</p>
            <p className="text-sm text-gray-700 mt-1">
              Vas a enviar la campaña a <strong>
                {confirming === 'both' ? 'Asesores + Inmobiliarias' : SEGMENT_LABEL[confirming]}
              </strong>. Esta acción no se puede deshacer (cada usuario solo puede recibir la campaña una vez).
            </p>
            <div className="mt-3 flex gap-2">
              <Button className="btn-habita rounded-full" onClick={() => send(confirming)} disabled={!!sending} data-testid="campaigns-confirm-yes">
                {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Send className="w-4 h-4 mr-1.5" />}
                Sí, enviar ahora
              </Button>
              <Button variant="outline" onClick={() => setConfirming(null)} disabled={!!sending} className="rounded-full" data-testid="campaigns-confirm-no">
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Last result */}
      {lastResult && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5" data-testid="campaigns-last-result">
          <p className="text-sm text-emerald-900 font-semibold mb-2">Resultado del último envío ({lastResult.dry_run ? 'simulación' : 'real'}):</p>
          <div className="grid sm:grid-cols-2 gap-3 text-xs">
            {Object.entries(lastResult.segments || {}).map(([seg, r]) => (
              <div key={seg} className="bg-white rounded-lg p-3 border border-emerald-100">
                <p className="font-bold text-habita-dark mb-1">{SEGMENT_LABEL[seg]}</p>
                <p className="text-gray-700">Elegibles: <strong>{r.eligible}</strong> · Ya enviados: <strong>{r.already_sent}</strong></p>
                <p className="text-gray-700">Enviados con éxito: <strong className="text-emerald-700">{r.sent_ok}</strong>{r.failed > 0 && <> · Fallidos: <strong className="text-rose-600">{r.failed}</strong></>}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* History */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="campaigns-history">
        <h3 className="font-serif text-lg font-bold text-habita-dark flex items-center gap-2 mb-3">
          <BarChart3 className="w-5 h-5 text-habita" /> Historial de campañas
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-gray-500 border-b border-gray-200">
                <th className="py-2 pr-3">Campaña</th>
                <th className="py-2 pr-3">Segmento</th>
                <th className="py-2 pr-3 text-right">Enviados</th>
                <th className="py-2 pr-3 text-right">Abiertos</th>
                <th className="py-2 pr-3 text-right">Clicks</th>
                <th className="py-2 pr-3">Último envío</th>
              </tr>
            </thead>
            <tbody>
              {history.map(c => {
                const openRate = c.sent_count ? Math.round((c.open_count / c.sent_count) * 100) : 0;
                const clickRate = c.sent_count ? Math.round((c.click_count / c.sent_count) * 100) : 0;
                return (
                  <tr key={c.campaign_id} className="border-b border-gray-100 last:border-0" data-testid={`campaigns-row-${c.campaign_id}`}>
                    <td className="py-3 pr-3 min-w-[260px]">
                      <p className="font-semibold text-habita-dark truncate">{c.subject}</p>
                      <p className="text-[10px] text-gray-400 font-mono mt-0.5 truncate">{c.campaign_id}</p>
                    </td>
                    <td className="py-3 pr-3">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${c.segment === 'agents' ? 'bg-habita-mint text-habita' : 'bg-amber-100 text-amber-700'}`}>
                        {SEGMENT_LABEL[c.segment]}
                      </span>
                    </td>
                    <td className="py-3 pr-3 text-right font-semibold text-habita-dark tabular-nums">{c.sent_count}</td>
                    <td className="py-3 pr-3 text-right tabular-nums" data-testid={`campaigns-opens-${c.segment}`}>
                      <span className="inline-flex items-center gap-1 text-emerald-700"><Eye className="w-3 h-3" /> {c.open_count}</span>
                      {c.sent_count > 0 && <span className="text-[10px] text-gray-400 ml-1">({openRate}%)</span>}
                    </td>
                    <td className="py-3 pr-3 text-right tabular-nums" data-testid={`campaigns-clicks-${c.segment}`}>
                      <span className="inline-flex items-center gap-1 text-habita"><MousePointerClick className="w-3 h-3" /> {c.click_count}</span>
                      {c.sent_count > 0 && <span className="text-[10px] text-gray-400 ml-1">({clickRate}%)</span>}
                    </td>
                    <td className="py-3 pr-3 text-xs text-gray-600">
                      {c.last_sent_at ? new Date(c.last_sent_at).toLocaleString('es-CO') : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function AudienceCard({ icon, label, counts, target, sending, onSend }) {
  const remaining = counts?.remaining ?? 0;
  const total = counts?.total ?? 0;
  const sent = counts?.already_sent ?? 0;
  const disabled = !!sending || remaining === 0;
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid={`campaigns-audience-${target}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-habita-dark">
          <span className="w-9 h-9 rounded-xl bg-habita-mint flex items-center justify-center text-habita">{icon}</span>
          <p className="font-serif font-bold text-lg">{label}</p>
        </div>
        <span className="text-3xl font-serif font-bold text-habita-dark tabular-nums" data-testid={`campaigns-total-${target}`}>{total}</span>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-4 text-xs">
        <div className="bg-habita-cream rounded-lg px-3 py-2">
          <p className="text-gray-500">Ya enviados</p>
          <p className="font-bold text-habita-dark text-base tabular-nums" data-testid={`campaigns-sent-${target}`}>{sent}</p>
        </div>
        <div className={`rounded-lg px-3 py-2 ${remaining > 0 ? 'bg-emerald-50' : 'bg-gray-50'}`}>
          <p className="text-gray-500">Pendientes</p>
          <p className={`font-bold text-base tabular-nums ${remaining > 0 ? 'text-emerald-700' : 'text-gray-400'}`} data-testid={`campaigns-remaining-${target}`}>{remaining}</p>
        </div>
      </div>
      <Button
        className="btn-habita rounded-full w-full mt-4"
        disabled={disabled}
        onClick={onSend}
        data-testid={`campaigns-send-${target}-btn`}
      >
        {sending === target
          ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" /> Enviando…</>
          : remaining === 0
            ? 'Todos ya recibieron'
            : <><Send className="w-4 h-4 mr-1.5" /> Enviar a {remaining} {label.toLowerCase()}</>}
      </Button>
    </div>
  );
}
